import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc,
  query, where, Timestamp, orderBy,
} from 'firebase/firestore';
import { Menu } from 'lucide-react';
import { db } from './firebase';
import DashboardNav from './DashboardNav';

// ─── helpers ─────────────────────────────────────────────────────────────────
const toDateStr = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const formatDisplay = (dateStr) => {
  const [y,m,day] = dateStr.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${day} ${months[parseInt(m)-1]} ${y}`;
};
const daysDiff = (dateStr) => {
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(dateStr); d.setHours(0,0,0,0);
  return Math.floor((today - d) / 86400000);
};
const yesterday = () => { const d = new Date(); d.setDate(d.getDate()-1); return toDateStr(d); };
const MAX_EDITABLE_DAYS = 7;

// ─── platform config ──────────────────────────────────────────────────────────
const PLATFORM_PRESETS = [
  { name: 'Facebook',   color: 'bg-blue-600',    text: 'text-white', icon: '𝑓',  ring: 'ring-blue-500' },
  { name: 'Google',     color: 'bg-red-500',     text: 'text-white', icon: 'G',  ring: 'ring-red-400' },
  { name: 'TikTok',     color: 'bg-zinc-800',    text: 'text-white', icon: '♪',  ring: 'ring-zinc-400' },
  { name: 'Instagram',  color: 'bg-pink-600',    text: 'text-white', icon: '◎',  ring: 'ring-pink-400' },
  { name: 'YouTube',    color: 'bg-red-600',     text: 'text-white', icon: '▶',  ring: 'ring-red-500' },
  { name: 'Twitter/X',  color: 'bg-neutral-900', text: 'text-white', icon: '✕',  ring: 'ring-gray-400' },
  { name: 'LinkedIn',   color: 'bg-sky-700',     text: 'text-white', icon: 'in', ring: 'ring-sky-500' },
  { name: 'Snapchat',   color: 'bg-yellow-400',  text: 'text-black', icon: '👻', ring: 'ring-yellow-400' },
  { name: 'WhatsApp',   color: 'bg-green-600',   text: 'text-white', icon: '💬', ring: 'ring-green-500' },
];
const getPlatformCfg = (name) =>
  PLATFORM_PRESETS.find(p => p.name === name) ||
  { name, color: 'bg-violet-700', text: 'text-white', icon: name[0].toUpperCase(), ring: 'ring-violet-400' };

// ─── Sub-components ───────────────────────────────────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  return (
    <div className={`fixed top-5 right-5 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-2xl
      shadow-md text-sm font-bold backdrop-blur-xl border animate-fade-in
      ${toast.type === 'error'
        ? 'bg-red-50 border-red-200 text-red-700'
        : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
      <span className="text-lg">{toast.type === 'error' ? '⚠️' : '✅'}</span>
      {toast.msg}
    </div>
  );
};

const StatCard = ({ icon, label, value, sub, accent }) => (
  <div className={`relative overflow-hidden rounded-2xl p-5 border border-gray-200 bg-gradient-to-br ${accent} group cursor-default`}>
    <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white group-hover:bg-orange-50 transition-all duration-500" />
    <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-gray-500 mb-3 flex items-center gap-1.5">
      <span>{icon}</span>{label}
    </p>
    <p className="text-2xl font-black text-gray-900 tracking-tight leading-none">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-1.5 font-medium">{sub}</p>}
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdCostPage() {
  const [entries, setEntries]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmit]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024);

  // form fields
  const [selectedDate, setDate]   = useState(yesterday());
  const [platforms, setPlatforms] = useState([]);
  const [customPlatform, setCust] = useState('');
  const [amountUSD, setUSD]       = useState('');
  const [exchangeRate, setRate]   = useState('');
  const [autoRate, setAutoRate]   = useState(null);
  const [useManual, setUseManual] = useState(false);
  const [note, setNote]           = useState('');
  const [rateLoading, setRateL]   = useState(false);
  const [toast, setToast]         = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // fetch entries
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'ad_cost'), orderBy('date', 'desc')));
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { showToast('Failed to load entries', 'error'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // fetch live exchange rate
  const fetchLiveRate = async () => {
    setRateL(true);
    try {
      const res  = await fetch('https://open.er-api.com/v6/latest/USD');
      const json = await res.json();
      const r    = json.rates?.BDT;
      if (r) { setAutoRate(r); if (!useManual) setRate(r.toFixed(2)); }
    } catch { showToast('Could not fetch exchange rate. Enter manually.', 'error'); }
    setRateL(false);
  };

  useEffect(() => { if (showForm) fetchLiveRate(); }, [showForm]);

  // auto-generate note
  useEffect(() => {
    if (platforms.length > 0 && selectedDate)
      setNote(`Ad Cost - ${platforms.join(', ')} - ${formatDisplay(selectedDate)}`);
  }, [platforms, selectedDate]);

  const togglePlatform = (p) =>
    setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const addCustom = () => {
    const t = customPlatform.trim();
    if (t && !platforms.includes(t)) { setPlatforms(prev => [...prev, t]); setCust(''); }
  };

  const entryForDate = (d) => entries.find(e => e.date === d);

  const getOrdersForDate = async (dateStr) => {
    const start = new Date(dateStr); start.setHours(0,0,0,0);
    const end   = new Date(dateStr); end.setHours(23,59,59,999);
    const snap  = await getDocs(query(
      collection(db, 'orders'),
      where('created_at', '>=', Timestamp.fromDate(start)),
      where('created_at', '<=', Timestamp.fromDate(end))
    ));
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(o =>
        !(o.orderId && String(o.orderId).startsWith('RORD')) &&
        o.status !== 'Cancel'
      );
  };

  const handleSubmit = async () => {
    if (!selectedDate)                       return showToast('Select a date', 'error');
    if (platforms.length === 0)              return showToast('Select at least one platform', 'error');
    if (!amountUSD || isNaN(+amountUSD))     return showToast('Enter valid USD amount', 'error');
    const r = parseFloat(exchangeRate);
    if (!r || isNaN(r))                      return showToast('Enter valid exchange rate', 'error');
    if (daysDiff(selectedDate) <= 0)         return showToast('Cannot add for today or future', 'error');
    if (!editingId && entryForDate(selectedDate)) return showToast('Entry exists. Edit instead.', 'error');

    setSubmit(true);
    try {
      const usd              = parseFloat(amountUSD);
      const bdt              = parseFloat((usd * r).toFixed(2));
      const orders           = await getOrdersForDate(selectedDate);
      const totalOrders      = orders.length;
      const orderIds         = orders.map(o => o.orderId || o.id);
      const costPerOrderBDT  = totalOrders > 0 ? +(bdt / totalOrders).toFixed(2) : 0;
      const costPerOrderUSD  = totalOrders > 0 ? +(usd / totalOrders).toFixed(4) : 0;

      const payload = {
        date: selectedDate, platforms,
        amount_usd: usd, amount_bdt: bdt, exchange_rate: r,
        total_orders: totalOrders, order_ids: orderIds,
        cost_per_order_bdt: costPerOrderBDT,
        cost_per_order_usd: costPerOrderUSD,
        note, updated_at: Timestamp.now(),
      };

      if (editingId) {
        await updateDoc(doc(db, 'ad_cost', editingId), payload);
        showToast('Entry updated! ✨');
      } else {
        payload.created_at = Timestamp.now();
        await addDoc(collection(db, 'ad_cost'), payload);
        await addDoc(collection(db, 'Credit'), {
          amount: bdt, note, timestamp: Timestamp.now(), type: 'credit', date: selectedDate,
        });
        const compRef  = doc(db, 'company', 'company_data');
        const compSnap = await getDoc(compRef);
        if (compSnap.exists()) {
          await updateDoc(compRef, { current_balance: (compSnap.data().current_balance || 0) - bdt });
        }
        showToast('Saved & balance updated! 🎉');
      }
      await fetchEntries();
      resetForm();
      setShowForm(false);
    } catch (e) { showToast('Error: ' + e.message, 'error'); }
    setSubmit(false);
  };

  const resetForm = () => {
    setDate(yesterday()); setPlatforms([]); setUSD(''); setNote('');
    setEditingId(null); setCust(''); setUseManual(false);
    if (autoRate) setRate(autoRate.toFixed(2));
  };

  const startEdit = (entry) => {
    if (daysDiff(entry.date) > MAX_EDITABLE_DAYS)
      return showToast('Entries older than 7 days cannot be edited', 'error');
    setEditingId(entry.id);
    setDate(entry.date);
    setPlatforms(entry.platforms || []);
    setUSD(String(entry.amount_usd));
    setRate(String(entry.exchange_rate));
    setNote(entry.note || '');
    setShowForm(true);
  };

  // computed
  const totalBDT       = entries.reduce((s,e) => s + (e.amount_bdt||0), 0);
  const totalUSD       = entries.reduce((s,e) => s + (e.amount_usd||0), 0);
  const validEntries   = entries.filter(e => e.total_orders > 0);
  const avgCPO         = validEntries.length
    ? validEntries.reduce((s,e) => s + e.cost_per_order_bdt, 0) / validEntries.length : 0;
  const existingEntry  = !editingId && entryForDate(selectedDate);
  const convertedBDT   = amountUSD && exchangeRate
    ? (parseFloat(amountUSD||0) * parseFloat(exchangeRate||0)) : 0;

  return (
    <div className="flex min-h-screen bg-gray-50 text-gray-900"
      style={{fontFamily:"'Plus Jakarta Sans','Outfit',system-ui,sans-serif"}}>

      <DashboardNav sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 min-w-0 relative overflow-x-hidden">

      <Toast toast={toast} />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 py-8">

        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 mb-4 hover:bg-orange-50 rounded-lg transition"
          title="Toggle menu"
        >
          <Menu size={24} />
        </button>

        {/* ─── Header ─── */}
        <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200">
              <div className="w-5 h-5 rounded-md bg-gradient-to-br text-white from-orange-400 to-orange-500 flex items-center justify-center text-[10px] font-black">₳</div>
              <span className="text-[11px] font-bold tracking-widest uppercase text-orange-600">ElevateMart</span>
            </div>
            <h1 className="text-[2.8rem] sm:text-[3.5rem] font-black leading-none tracking-[-0.03em] mb-2">
              Ad Cost
              <br />
              <span className="bg-gradient-to-r from-orange-400 via-orange-400 to-orange-500 bg-clip-text text-transparent">
                Manager
              </span>
            </h1>
            <p className="text-gray-400 text-sm font-medium">Track ad spend · auto-sync Firebase balance · per-order cost analytics</p>
          </div>

          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="group relative inline-flex items-center gap-3 px-7 py-4 rounded-2xl font-bold text-sm
              bg-gradient-to-br text-white from-orange-400 to-orange-500
              hover:from-orange-500 hover:to-orange-600
              shadow-md shadow-orange-200/60 hover:shadow-orange-200/60
              transition-all duration-300 hover:-translate-y-1 active:translate-y-0
              border border-orange-200 overflow-hidden whitespace-nowrap self-end sm:self-auto"
          >
            <span className="relative z-10 text-xl leading-none font-black">+</span>
            <span className="relative z-10">New Ad Entry</span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0
              translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
          </button>
        </header>

        {/* ─── Stat Cards ─── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon="৳" label="Total Spent BDT"
            value={`৳${totalBDT.toLocaleString('en-BD',{maximumFractionDigits:0})}`}
            sub={`across ${entries.length} campaigns`}
            accent="from-orange-50 to-white"
          />
          <StatCard
            icon="$" label="Total Spent USD"
            value={`$${totalUSD.toFixed(2)}`}
            sub="all platforms combined"
            accent="from-amber-50 to-white"
          />
          <StatCard
            icon="📦" label="Avg Cost / Order"
            value={`৳${avgCPO.toFixed(1)}`}
            sub="average across all days"
            accent="from-yellow-50 to-white"
          />
          <StatCard
            icon="📅" label="Days Tracked"
            value={`${entries.length}`}
            sub="total days with ad spend"
            accent="from-orange-100 to-orange-50"
          />
        </div>

        {/* ─── Table Card ─── */}
        <div className="rounded-3xl border border-gray-200 bg-white backdrop-blur-2xl overflow-hidden shadow-md shadow-black/40">

          {/* table header bar */}
          <div className="flex items-center justify-between px-7 py-5 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-orange-500 text-white" />
                <div className="w-2.5 h-2.5 rounded-full bg-orange-50" />
                <div className="w-2.5 h-2.5 rounded-full bg-orange-50" />
              </div>
              <h2 className="text-base font-bold text-gray-700">Ad Cost History</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-xs text-gray-400 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
                {entries.length} {entries.length === 1 ? 'entry' : 'entries'} · 7-day edit window
              </span>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-28 gap-4">
              <div className="relative w-12 h-12">
                <div className="absolute inset-0 border-2 border-orange-200 rounded-full" />
                <div className="absolute inset-0 border-2 border-t-violet-400 rounded-full animate-spin" />
              </div>
              <p className="text-gray-400 text-sm font-medium">Loading entries...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-28 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-orange-50 border border-orange-200 flex items-center justify-center text-3xl">📊</div>
              <div className="text-center">
                <p className="text-gray-500 font-bold mb-1">No entries yet</p>
                <p className="text-gray-400 text-sm">Click "New Ad Entry" to add your first cost</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    {['Date','Platforms','USD','BDT','Orders','Cost / Order','Rate','Note',''].map(h => (
                      <th key={h} className="text-left px-6 py-4 text-[10px] font-black tracking-[0.18em] uppercase text-gray-400 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {entries.map((entry) => {
                    const diff    = daysDiff(entry.date);
                    const canEdit = diff <= MAX_EDITABLE_DAYS;
                    return (
                      <tr key={entry.id} className="group hover:bg-orange-50 transition-colors duration-150">
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className="font-bold text-gray-700 text-sm whitespace-nowrap">{formatDisplay(entry.date)}</span>
                            {diff <= 1
                              ? <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-emerald-400 inline-block animate-pulse" />Recent</span>
                              : canEdit
                                ? <span className="text-[10px] text-amber-600">Editable</span>
                                : <span className="text-[10px] text-gray-400">🔒 Locked</span>
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1.5">
                            {(entry.platforms||[]).map(p => {
                              const cfg = getPlatformCfg(p);
                              return (
                                <span key={p} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold ${cfg.color} ${cfg.text}`}>
                                  <span className="text-[10px] opacity-80">{cfg.icon}</span>
                                  {p}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-orange-700 font-black text-base">${entry.amount_usd?.toFixed(2)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-emerald-700 font-black text-base">৳{entry.amount_bdt?.toLocaleString('en-BD')}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center font-black text-gray-700 text-sm">
                            {entry.total_orders}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-bold text-gray-700">৳{entry.cost_per_order_bdt?.toFixed(2)}</span>
                            <span className="text-[11px] text-gray-400 font-mono">${entry.cost_per_order_usd?.toFixed(4)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-[11px] text-gray-400 font-mono bg-white px-2.5 py-1 rounded-lg border border-gray-200 whitespace-nowrap">
                            {entry.exchange_rate} BDT
                          </span>
                        </td>
                        <td className="px-6 py-4 max-w-[160px]">
                          <span className="text-[11px] text-gray-400 truncate block" title={entry.note}>{entry.note}</span>
                        </td>
                        <td className="px-6 py-4">
                          {canEdit ? (
                            <button
                              onClick={() => startEdit(entry)}
                              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold
                                text-orange-700 bg-orange-50 border border-orange-200
                                hover:bg-orange-50 hover:border-orange-200
                                opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap">
                              ✏️ Edit
                            </button>
                          ) : (
                            <span className="text-[10px] text-gray-400 flex items-center gap-1 whitespace-nowrap">🔒 Locked</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ─── Modal ─── */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4
            bg-black/40 backdrop-blur-lg"
          onClick={e => { if (e.target === e.currentTarget) { setShowForm(false); resetForm(); } }}
        >
          <div className="w-full max-w-[580px] max-h-[94vh] overflow-y-auto rounded-3xl
            bg-white border border-gray-200
            shadow-[0_24px_60px_rgba(0,0,0,0.18)]">

            {/* Modal Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-xl z-10 px-8 pt-8 pb-6 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-orange-50 border border-orange-200 mb-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-orange-500 text-white animate-pulse" />
                    <span className="text-[10px] font-bold tracking-widest uppercase text-orange-600">
                      {editingId ? 'Edit Mode' : 'New Entry'}
                    </span>
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 leading-tight">
                    {editingId ? '✏️ Update Ad Cost' : '🚀 Add Ad Cost Entry'}
                  </h2>
                  <p className="text-gray-400 text-xs mt-1">
                    {editingId ? 'Update the entry below' : 'Saves to ad_cost, credit & updates company balance'}
                  </p>
                </div>
                <button
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-50
                    hover:bg-orange-50 text-gray-500 hover:text-gray-700
                    border border-gray-200 transition-all text-sm flex-shrink-0 mt-1">
                  ✕
                </button>
              </div>
            </div>

            {/* Form body */}
            <div className="px-8 py-6 flex flex-col gap-6">

              {/* Date picker */}
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black tracking-[0.2em] uppercase text-gray-500 flex items-center justify-between">
                  <span>📅 Date</span>
                  <span className="text-gray-400 normal-case tracking-normal font-normal text-[10px]">Previous dates only · today is blocked</span>
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  max={yesterday()}
                  onChange={e => { if (daysDiff(e.target.value) > 0) setDate(e.target.value); }}
                  disabled={!!editingId}
                  className={`w-full rounded-xl px-4 py-3 text-sm font-bold outline-none transition-all duration-200
                    bg-gray-50 border
                    ${editingId
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-200 text-gray-900 hover:border-orange-200 focus:border-orange-200 focus:bg-gray-50'}`}
                />
                {existingEntry && (
                  <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <span className="text-base mt-0.5">⚠️</span>
                    <p className="text-xs text-red-700 font-medium leading-relaxed">
                      An entry already exists for <strong>{formatDisplay(selectedDate)}</strong>. Please choose a different date or edit the existing entry.
                    </p>
                  </div>
                )}
              </div>

              {/* Platforms */}
              <div className="flex flex-col gap-3">
                <label className="text-[10px] font-black tracking-[0.2em] uppercase text-gray-500">📢 Ad Platforms</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORM_PRESETS.map(p => {
                    const active = platforms.includes(p.name);
                    return (
                      <button key={p.name} onClick={() => togglePlatform(p.name)}
                        className={`relative flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all duration-200
                          ${active
                            ? `${p.color} ${p.text} border-transparent shadow-lg ring-1 ${p.ring} ring-offset-1 ring-offset-white scale-[1.04]`
                            : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-orange-50 hover:text-gray-700 hover:border-gray-200'}`}>
                        <span className="text-[11px]">{p.icon}</span>
                        {p.name}
                        {active && <span className="text-[10px] opacity-75 ml-0.5">✓</span>}
                      </button>
                    );
                  })}
                </div>
                {/* custom platform */}
                <div className="flex gap-2">
                  <input
                    placeholder="Add custom platform (press Enter)..."
                    value={customPlatform}
                    onChange={e => setCust(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCustom()}
                    className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-2.5
                      text-sm text-gray-900 placeholder-gray-400 outline-none
                      focus:border-orange-200 focus:bg-gray-50 transition-all"
                  />
                  <button onClick={addCustom}
                    className="px-4 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold
                      hover:bg-orange-50 hover:border-orange-200 transition-all whitespace-nowrap">
                    + Add
                  </button>
                </div>
                {/* custom tags */}
                <div className="flex flex-wrap gap-1.5">
                  {platforms.filter(p => !PLATFORM_PRESETS.find(pp => pp.name === p)).map(p => (
                    <span key={p} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                      bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold">
                      {p}
                      <button onClick={() => setPlatforms(prev => prev.filter(x => x !== p))}
                        className="text-gray-400 hover:text-red-600 transition-colors text-[10px] leading-none">✕</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Amount USD */}
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black tracking-[0.2em] uppercase text-gray-500">💵 Ad Spend (USD)</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-black text-base pointer-events-none">$</div>
                  <input
                    type="number" placeholder="0.00" value={amountUSD}
                    onChange={e => setUSD(e.target.value)} min="0" step="0.01"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-9 pr-4 py-3
                      text-gray-900 text-base font-black outline-none
                      hover:border-orange-200 focus:border-orange-200 focus:bg-gray-50
                      transition-all placeholder-gray-400"
                  />
                </div>
              </div>

              {/* Exchange Rate */}
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black tracking-[0.2em] uppercase text-gray-500 flex items-center justify-between">
                  <span>💱 Exchange Rate (1 USD = ? BDT)</span>
                  <button onClick={() => { setUseManual(!useManual); if (useManual && autoRate) setRate(autoRate.toFixed(2)); }}
                    className={`text-[10px] font-bold px-2.5 py-1 rounded-lg border transition-all normal-case tracking-normal
                      ${useManual
                        ? 'text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-50'
                        : 'text-orange-600 bg-orange-50 border-orange-200 hover:bg-orange-50'}`}>
                    {useManual ? '🔄 Use live rate' : '✏️ Manual'}
                  </button>
                </label>
                <div className="relative">
                  <input
                    type="number" value={exchangeRate}
                    onChange={e => setRate(e.target.value)}
                    disabled={!useManual}
                    placeholder={rateLoading ? 'Fetching live rate…' : 'Rate'}
                    className={`w-full rounded-xl px-4 py-3 text-sm font-bold outline-none transition-all
                      bg-gray-50 border
                      ${useManual
                        ? 'border-amber-200 text-amber-700 focus:border-amber-200 focus:bg-gray-50'
                        : 'border-gray-200 text-gray-500 cursor-not-allowed'}`}
                  />
                  {rateLoading && (
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 border border-orange-200 border-t-violet-400 rounded-full animate-spin" />
                  )}
                </div>
                {autoRate && (
                  <div className="flex items-center gap-2 text-[11px] text-gray-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                    Live rate: <span className="text-emerald-600 font-bold">{autoRate.toFixed(2)} BDT</span> · open.er-api.com
                    {useManual && <span className="text-amber-600">· manual override active</span>}
                  </div>
                )}
                {convertedBDT > 0 && (
                  <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3.5">
                    <div>
                      <p className="text-[10px] text-emerald-600 font-bold tracking-widest uppercase mb-0.5">Converted Amount</p>
                      <p className="text-xs text-gray-400">${parseFloat(amountUSD).toFixed(2)} × {parseFloat(exchangeRate).toFixed(2)}</p>
                    </div>
                    <p className="text-emerald-700 font-black text-xl">
                      ৳{convertedBDT.toLocaleString('en-BD', {minimumFractionDigits:2})}
                    </p>
                  </div>
                )}
              </div>

              {/* Note */}
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black tracking-[0.2em] uppercase text-gray-500 flex items-center justify-between">
                  <span>📝 Credit Note</span>
                  <span className="text-gray-400 normal-case tracking-normal font-normal text-[10px]">Auto-generated · editable</span>
                </label>
                <input
                  type="text" value={note} onChange={e => setNote(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3
                    text-gray-900 text-sm outline-none
                    hover:border-orange-200 focus:border-orange-200 focus:bg-gray-50
                    transition-all placeholder-gray-400"
                  placeholder="Auto-generated based on platforms & date"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white/95 backdrop-blur-xl border-t border-gray-200 px-8 py-5 flex items-center justify-between gap-3">
              <p className="text-[11px] text-gray-400 hidden sm:block">
                {editingId
                  ? 'Only ad_cost will be updated (no balance change)'
                  : 'Will save to ad_cost, credit & deduct from balance'}
              </p>
              <div className="flex items-center gap-2.5 ml-auto">
                <button
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-500
                    bg-gray-50 hover:bg-orange-50 border border-gray-200
                    transition-all hover:text-gray-500">
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !!existingEntry}
                  className="relative flex items-center gap-2.5 px-7 py-2.5 rounded-xl text-sm font-bold text-white overflow-hidden
                    bg-gradient-to-r from-orange-400 to-orange-500
                    hover:from-orange-500 hover:to-orange-600
                    shadow-lg shadow-orange-200/60 hover:shadow-orange-200/60
                    transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0
                    disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none
                    border border-orange-200">
                  {submitting ? (
                    <><div className="w-4 h-4 border-2 border-gray-200 border-t-white rounded-full animate-spin" />Saving...</>
                  ) : editingId
                    ? '💾 Update Entry'
                    : '🚀 Save & Update Balance'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
